import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { ApClassApi } from "../../api/ApClassApi";
import type { ApClassAttendanceFetchOptions } from "../../generated/dto/ApClassAttendanceFetchOptions";
import type { ApClassAttendanceStaff } from "../../generated/dto/ApClassAttendanceStaff";
import type { ApClassFetchOptions } from "../../generated/dto/ApClassFetchOptions";
import type { ApClassFormatFetchOptions } from "../../generated/dto/ApClassFormatFetchOptions";
import type { ApClassFormatStaff } from "../../generated/dto/ApClassFormatStaff";
import type { ApClassMember } from "../../generated/dto/ApClassMember";
import type { ApClassRef } from "../../generated/dto/ApClassRef";
import type { ApClassSearchParams } from "../../generated/dto/ApClassSearchParams";
import type { ApClassSessionFetchOptions } from "../../generated/dto/ApClassSessionFetchOptions";
import type { ApClassSessionSearchParams } from "../../generated/dto/ApClassSessionSearchParams";
import type { ApClassSessionStaff } from "../../generated/dto/ApClassSessionStaff";
import type { ApClassSessionStaffSearchResult } from "../../generated/dto/ApClassSessionStaffSearchResult";
import type { ApClassSignupStaff } from "../../generated/dto/ApClassSignupStaff";
import type { ApClassSignupStaffFetchOptions } from "../../generated/dto/ApClassSignupStaffFetchOptions";
import type { ApClassStaff } from "../../generated/dto/ApClassStaff";
import type { ApClassStaffSearchResult } from "../../generated/dto/ApClassStaffSearchResult";
import type { ApClassTypeFetchOptions } from "../../generated/dto/ApClassTypeFetchOptions";
import type { ApClassTypeOverrideFetchOptions } from "../../generated/dto/ApClassTypeOverrideFetchOptions";
import type { ApClassTypeOverrideStaff } from "../../generated/dto/ApClassTypeOverrideStaff";
import type { ApClassTypeSearchParams } from "../../generated/dto/ApClassTypeSearchParams";
import type { ApClassTypeStaff } from "../../generated/dto/ApClassTypeStaff";
import type { ApClassTypeStaffSearchResult } from "../../generated/dto/ApClassTypeStaffSearchResult";
import type { ApClassWlResultFetchOptions } from "../../generated/dto/ApClassWlResultFetchOptions";
import type { ApClassWlResultStaff } from "../../generated/dto/ApClassWlResultStaff";
import type { ClassLocationFetchOptions } from "../../generated/dto/ClassLocationFetchOptions";
import type { ClassLocationStaff } from "../../generated/dto/ClassLocationStaff";
import type { CreateApClass } from "../../generated/dto/CreateApClass";
import type { CreateApClassAttendance } from "../../generated/dto/CreateApClassAttendance";
import type { CreateApClassFormat } from "../../generated/dto/CreateApClassFormat";
import type { CreateApClassSession } from "../../generated/dto/CreateApClassSession";
import type { CreateApClassSignup } from "../../generated/dto/CreateApClassSignup";
import type { CreateApClassType } from "../../generated/dto/CreateApClassType";
import type { CreateApClassTypeOverride } from "../../generated/dto/CreateApClassTypeOverride";
import type { CreateApClassWlResult } from "../../generated/dto/CreateApClassWlResult";
import type { DeleteApClassAttendanceStaff } from "../../generated/dto/DeleteApClassAttendanceStaff";
import type { DeleteApClassFormatStaff } from "../../generated/dto/DeleteApClassFormatStaff";
import type { DeleteApClassSessionStaff } from "../../generated/dto/DeleteApClassSessionStaff";
import type { DeleteApClassSignupStaff } from "../../generated/dto/DeleteApClassSignupStaff";
import type { DeleteApClassStaff } from "../../generated/dto/DeleteApClassStaff";
import type { DeleteApClassTypeOverrideStaff } from "../../generated/dto/DeleteApClassTypeOverrideStaff";
import type { DeleteApClassTypeStaff } from "../../generated/dto/DeleteApClassTypeStaff";
import type { DeleteApClassWlResultStaff } from "../../generated/dto/DeleteApClassWlResultStaff";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { RatingFetchOptions } from "../../generated/dto/RatingFetchOptions";
import type { RatingStaff } from "../../generated/dto/RatingStaff";
import type { UpdateApClass } from "../../generated/dto/UpdateApClass";
import type { UpdateApClassAttendance } from "../../generated/dto/UpdateApClassAttendance";
import type { UpdateApClassFormat } from "../../generated/dto/UpdateApClassFormat";
import type { UpdateApClassSession } from "../../generated/dto/UpdateApClassSession";
import type { UpdateApClassSignup } from "../../generated/dto/UpdateApClassSignup";
import type { UpdateApClassType } from "../../generated/dto/UpdateApClassType";
import type { UpdateApClassTypeOverride } from "../../generated/dto/UpdateApClassTypeOverride";
import type { UpdateApClassWlResult } from "../../generated/dto/UpdateApClassWlResult";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class ApClassApiClient implements ApClassApi {
  constructor(private readonly client = useClient()) {}

  async getApClassTypeStaff(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<ApClassTypeStaff>(data, "generated.dto.ApClassTypeStaff");
  }

  async getApClassTypeOverrideStaff(id: number, options?: ApClassTypeOverrideFetchOptions): Promise<ApClassTypeOverrideStaff> {
    const { data } = await this.client.get<unknown>(`/apclasstypeoverride/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeOverrideFetchOptions") });
    return deserializeCbiDto<ApClassTypeOverrideStaff>(data, "generated.dto.ApClassTypeOverrideStaff");
  }

  async getApClassFormatStaff(id: number, options?: ApClassFormatFetchOptions): Promise<ApClassFormatStaff> {
    const { data } = await this.client.get<unknown>(`/apclassformat/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ApClassFormatFetchOptions") });
    return deserializeCbiDto<ApClassFormatStaff>(data, "generated.dto.ApClassFormatStaff");
  }

  async getApClassStaff(id: number, options?: ApClassFetchOptions): Promise<ApClassStaff> {
    const { data } = await this.client.get<unknown>(`/apclass/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ApClassFetchOptions") });
    return deserializeCbiDto<ApClassStaff>(data, "generated.dto.ApClassStaff");
  }

  async getApClassSessionStaff(id: number, options?: ApClassSessionFetchOptions): Promise<ApClassSessionStaff> {
    const { data } = await this.client.get<unknown>(`/apclasssession/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ApClassSessionFetchOptions") });
    return deserializeCbiDto<ApClassSessionStaff>(data, "generated.dto.ApClassSessionStaff");
  }

  async getApClassSignupStaff(id: number, options?: ApClassSignupStaffFetchOptions): Promise<ApClassSignupStaff> {
    const { data } = await this.client.get<unknown>(`/apclasssignup/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ApClassSignupStaffFetchOptions") });
    return deserializeCbiDto<ApClassSignupStaff>(data, "generated.dto.ApClassSignupStaff");
  }

  async getApClassAttendanceStaff(id: number, options?: ApClassAttendanceFetchOptions): Promise<ApClassAttendanceStaff> {
    const { data } = await this.client.get<unknown>(`/apclassattendance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ApClassAttendanceFetchOptions") });
    return deserializeCbiDto<ApClassAttendanceStaff>(data, "generated.dto.ApClassAttendanceStaff");
  }

  async getApClassWlResultStaff(id: number, options?: ApClassWlResultFetchOptions): Promise<ApClassWlResultStaff> {
    const { data } = await this.client.get<unknown>(`/apclasswlresult/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ApClassWlResultFetchOptions") });
    return deserializeCbiDto<ApClassWlResultStaff>(data, "generated.dto.ApClassWlResultStaff");
  }

  async createApClassTypeStaff(source: CreateApClassType): Promise<ApClassTypeStaff> {
    const { data } = await this.client.post<unknown>(`/apclasstype/staff`, serializeCbiDto(source, "generated.dto.CreateApClassType"));
    return deserializeCbiDto<ApClassTypeStaff>(data, "generated.dto.ApClassTypeStaff");
  }

  async createApClassTypeOverrideStaff(source: CreateApClassTypeOverride): Promise<ApClassTypeOverrideStaff> {
    const { data } = await this.client.post<unknown>(`/apclasstypeoverride/staff`, serializeCbiDto(source, "generated.dto.CreateApClassTypeOverride"));
    return deserializeCbiDto<ApClassTypeOverrideStaff>(data, "generated.dto.ApClassTypeOverrideStaff");
  }

  async createApClassFormatStaff(source: CreateApClassFormat): Promise<ApClassFormatStaff> {
    const { data } = await this.client.post<unknown>(`/apclassformat/staff`, serializeCbiDto(source, "generated.dto.CreateApClassFormat"));
    return deserializeCbiDto<ApClassFormatStaff>(data, "generated.dto.ApClassFormatStaff");
  }

  async createApClassStaff(source: CreateApClass): Promise<ApClassStaff> {
    const { data } = await this.client.post<unknown>(`/apclass/staff`, serializeCbiDto(source, "generated.dto.CreateApClass"));
    return deserializeCbiDto<ApClassStaff>(data, "generated.dto.ApClassStaff");
  }

  async createApClassSessionStaff(source: CreateApClassSession): Promise<ApClassSessionStaff> {
    const { data } = await this.client.post<unknown>(`/apclasssession/staff`, serializeCbiDto(source, "generated.dto.CreateApClassSession"));
    return deserializeCbiDto<ApClassSessionStaff>(data, "generated.dto.ApClassSessionStaff");
  }

  async createApClassSignupStaff(source: CreateApClassSignup): Promise<ApClassSignupStaff> {
    const { data } = await this.client.post<unknown>(`/apclasssignup/staff`, serializeCbiDto(source, "generated.dto.CreateApClassSignup"));
    return deserializeCbiDto<ApClassSignupStaff>(data, "generated.dto.ApClassSignupStaff");
  }

  async createApClassAttendanceStaff(source: CreateApClassAttendance): Promise<ApClassAttendanceStaff> {
    const { data } = await this.client.post<unknown>(`/apclassattendance/staff`, serializeCbiDto(source, "generated.dto.CreateApClassAttendance"));
    return deserializeCbiDto<ApClassAttendanceStaff>(data, "generated.dto.ApClassAttendanceStaff");
  }

  async createApClassWlResultStaff(source: CreateApClassWlResult): Promise<ApClassWlResultStaff> {
    const { data } = await this.client.post<unknown>(`/apclasswlresult/staff`, serializeCbiDto(source, "generated.dto.CreateApClassWlResult"));
    return deserializeCbiDto<ApClassWlResultStaff>(data, "generated.dto.ApClassWlResultStaff");
  }

  async updateApClassTypeStaff(source: UpdateApClassType): Promise<ApClassTypeStaff> {
    const { data } = await this.client.put<unknown>(`/apclasstype/staff/${source.typeId}`, serializeCbiDto(source, "generated.dto.UpdateApClassType"));
    return deserializeCbiDto<ApClassTypeStaff>(data, "generated.dto.ApClassTypeStaff");
  }

  async updateApClassTypeOverrideStaff(source: UpdateApClassTypeOverride): Promise<ApClassTypeOverrideStaff> {
    const { data } = await this.client.put<unknown>(`/apclasstypeoverride/staff/${source.rowId}`, serializeCbiDto(source, "generated.dto.UpdateApClassTypeOverride"));
    return deserializeCbiDto<ApClassTypeOverrideStaff>(data, "generated.dto.ApClassTypeOverrideStaff");
  }

  async updateApClassFormatStaff(source: UpdateApClassFormat): Promise<ApClassFormatStaff> {
    const { data } = await this.client.put<unknown>(`/apclassformat/staff/${source.formatId}`, serializeCbiDto(source, "generated.dto.UpdateApClassFormat"));
    return deserializeCbiDto<ApClassFormatStaff>(data, "generated.dto.ApClassFormatStaff");
  }

  async updateApClassStaff(source: UpdateApClass): Promise<ApClassStaff> {
    const { data } = await this.client.put<unknown>(`/apclass/staff/${source.instanceId}`, serializeCbiDto(source, "generated.dto.UpdateApClass"));
    return deserializeCbiDto<ApClassStaff>(data, "generated.dto.ApClassStaff");
  }

  async updateApClassSessionStaff(source: UpdateApClassSession): Promise<ApClassSessionStaff> {
    const { data } = await this.client.put<unknown>(`/apclasssession/staff/${source.sessionId}`, serializeCbiDto(source, "generated.dto.UpdateApClassSession"));
    return deserializeCbiDto<ApClassSessionStaff>(data, "generated.dto.ApClassSessionStaff");
  }

  async updateApClassSignupStaff(source: UpdateApClassSignup): Promise<ApClassSignupStaff> {
    const { data } = await this.client.put<unknown>(`/apclasssignup/staff/${source.signupId}`, serializeCbiDto(source, "generated.dto.UpdateApClassSignup"));
    return deserializeCbiDto<ApClassSignupStaff>(data, "generated.dto.ApClassSignupStaff");
  }

  async updateApClassAttendanceStaff(source: UpdateApClassAttendance): Promise<ApClassAttendanceStaff> {
    const { data } = await this.client.put<unknown>(`/apclassattendance/staff/${source.attendId}`, serializeCbiDto(source, "generated.dto.UpdateApClassAttendance"));
    return deserializeCbiDto<ApClassAttendanceStaff>(data, "generated.dto.ApClassAttendanceStaff");
  }

  async updateApClassWlResultStaff(source: UpdateApClassWlResult): Promise<ApClassWlResultStaff> {
    const { data } = await this.client.put<unknown>(`/apclasswlresult/staff/${source.signupId}`, serializeCbiDto(source, "generated.dto.UpdateApClassWlResult"));
    return deserializeCbiDto<ApClassWlResultStaff>(data, "generated.dto.ApClassWlResultStaff");
  }

  async deleteApClassTypeStaff(source: DeleteApClassTypeStaff): Promise<void> {
    await this.client.delete(`/apclasstype/staff/${source.typeId}`);
  }

  async deleteApClassTypeOverrideStaff(source: DeleteApClassTypeOverrideStaff): Promise<void> {
    await this.client.delete(`/apclasstypeoverride/staff/${source.rowId}`);
  }

  async deleteApClassFormatStaff(source: DeleteApClassFormatStaff): Promise<void> {
    await this.client.delete(`/apclassformat/staff/${source.formatId}`);
  }

  async deleteApClassStaff(source: DeleteApClassStaff): Promise<void> {
    await this.client.delete(`/apclass/staff/${source.instanceId}`);
  }

  async deleteApClassSessionStaff(source: DeleteApClassSessionStaff): Promise<void> {
    await this.client.delete(`/apclasssession/staff/${source.sessionId}`);
  }

  async deleteApClassSignupStaff(source: DeleteApClassSignupStaff): Promise<void> {
    await this.client.delete(`/apclasssignup/staff/${source.signupId}`);
  }

  async deleteApClassAttendanceStaff(source: DeleteApClassAttendanceStaff): Promise<void> {
    await this.client.delete(`/apclassattendance/staff/${source.attendId}`);
  }

  async deleteApClassWlResultStaff(source: DeleteApClassWlResultStaff): Promise<void> {
    await this.client.delete(`/apclasswlresult/staff/${source.signupId}`);
  }

  async searchApClassTypeStaff(params: ApClassTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<ApClassTypeStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/apclasstype/staff/search`, serializeCbiDto(params, "generated.dto.ApClassTypeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<ApClassTypeStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<ApClassTypeStaffSearchResult>>(result.items, "generated.dto.ApClassTypeStaffSearchResult") };
    return result;
  }

  async searchApClassStaff(params: ApClassSearchParams, page: number, perPage: number): Promise<PaginationResult<ApClassStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/apclass/staff/search`, serializeCbiDto(params, "generated.dto.ApClassSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<ApClassStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<ApClassStaffSearchResult>>(result.items, "generated.dto.ApClassStaffSearchResult") };
    return result;
  }

  async searchApClassSessionStaff(params: ApClassSessionSearchParams, page: number, perPage: number): Promise<PaginationResult<ApClassSessionStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/apclasssession/staff/search`, serializeCbiDto(params, "generated.dto.ApClassSessionSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<ApClassSessionStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<ApClassSessionStaffSearchResult>>(result.items, "generated.dto.ApClassSessionStaffSearchResult") };
    return result;
  }

  async searchApClassTypeStaffFull(params: ApClassTypeSearchParams, page: number, perPage: number, options?: ApClassTypeFetchOptions): Promise<PaginationResult<ApClassTypeStaff>> {
    const { data } = await this.client.post<unknown>(`/apclasstype/staff/search/full`, serializeCbiDto(params, "generated.dto.ApClassTypeSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<ApClassTypeStaff>;
    result = { ...result, items: deserializeCbiDto<Array<ApClassTypeStaff>>(result.items, "generated.dto.ApClassTypeStaff") };
    return result;
  }

  async searchApClassStaffFull(params: ApClassSearchParams, page: number, perPage: number, options?: ApClassFetchOptions): Promise<PaginationResult<ApClassStaff>> {
    const { data } = await this.client.post<unknown>(`/apclass/staff/search/full`, serializeCbiDto(params, "generated.dto.ApClassSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.ApClassFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<ApClassStaff>;
    result = { ...result, items: deserializeCbiDto<Array<ApClassStaff>>(result.items, "generated.dto.ApClassStaff") };
    return result;
  }

  async searchApClassSessionStaffFull(params: ApClassSessionSearchParams, page: number, perPage: number, options?: ApClassSessionFetchOptions): Promise<PaginationResult<ApClassSessionStaff>> {
    const { data } = await this.client.post<unknown>(`/apclasssession/staff/search/full`, serializeCbiDto(params, "generated.dto.ApClassSessionSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.ApClassSessionFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<ApClassSessionStaff>;
    result = { ...result, items: deserializeCbiDto<Array<ApClassSessionStaff>>(result.items, "generated.dto.ApClassSessionStaff") };
    return result;
  }

  async getApClassTypeStaffRatingPrereqId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/ratingprereqid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getApClassTypeStaffRatingOverkillId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/ratingoverkillid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getApClassTypeStaffClassPrereqId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/classprereqid`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<ApClassTypeStaff | null>(data, "generated.dto.ApClassTypeStaff");
  }

  async getApClassTypeStaffClassOverkillId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/classoverkillid`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<ApClassTypeStaff | null>(data, "generated.dto.ApClassTypeStaff");
  }

  async getApClassTypeStaffTeachRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/teachratingid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getApClassTypeStaffFormats(id: number, options?: ApClassFormatFetchOptions): Promise<Array<ApClassFormatStaff>> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/formats`, { params: serializeCbiDto(options, "generated.dto.ApClassFormatFetchOptions") });
    return deserializeCbiDto<Array<ApClassFormatStaff>>(data, "generated.dto.ApClassFormatStaff");
  }

  async getApClassTypeStaffTypesRequiringThisAsPrereq(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/typesrequiringthisasprereq`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<Array<ApClassTypeStaff>>(data, "generated.dto.ApClassTypeStaff");
  }

  async getApClassTypeStaffTypesRequiringThisAsOverkill(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/typesrequiringthisasoverkill`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<Array<ApClassTypeStaff>>(data, "generated.dto.ApClassTypeStaff");
  }

  async getApClassTypeStaffClasses(id: number, options?: ApClassFetchOptions): Promise<Array<ApClassStaff>> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/classes`, { params: serializeCbiDto(options, "generated.dto.ApClassFetchOptions") });
    return deserializeCbiDto<Array<ApClassStaff>>(data, "generated.dto.ApClassStaff");
  }

  async getApClassTypeStaffOverrides(id: number, options?: ApClassTypeOverrideFetchOptions): Promise<Array<ApClassTypeOverrideStaff>> {
    const { data } = await this.client.get<unknown>(`/apclasstype/staff/${id}/overrides`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeOverrideFetchOptions") });
    return deserializeCbiDto<Array<ApClassTypeOverrideStaff>>(data, "generated.dto.ApClassTypeOverrideStaff");
  }

  async getApClassTypeOverrideStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/apclasstypeoverride/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getApClassTypeOverrideStaffTypeId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff> {
    const { data } = await this.client.get<unknown>(`/apclasstypeoverride/staff/${id}/typeid`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<ApClassTypeStaff>(data, "generated.dto.ApClassTypeStaff");
  }

  async getApClassFormatStaffTypeId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff> {
    const { data } = await this.client.get<unknown>(`/apclassformat/staff/${id}/typeid`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<ApClassTypeStaff>(data, "generated.dto.ApClassTypeStaff");
  }

  async getApClassFormatStaffInstances(id: number, options?: ApClassFetchOptions): Promise<Array<ApClassStaff>> {
    const { data } = await this.client.get<unknown>(`/apclassformat/staff/${id}/instances`, { params: serializeCbiDto(options, "generated.dto.ApClassFetchOptions") });
    return deserializeCbiDto<Array<ApClassStaff>>(data, "generated.dto.ApClassStaff");
  }

  async getApClassStaffFormatId(id: number, options?: ApClassFormatFetchOptions): Promise<ApClassFormatStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclass/staff/${id}/formatid`, { params: serializeCbiDto(options, "generated.dto.ApClassFormatFetchOptions") });
    return deserializeCbiDto<ApClassFormatStaff | null>(data, "generated.dto.ApClassFormatStaff");
  }

  async getApClassStaffLocationId(id: number, options?: ClassLocationFetchOptions): Promise<ClassLocationStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclass/staff/${id}/locationid`, { params: serializeCbiDto(options, "generated.dto.ClassLocationFetchOptions") });
    return deserializeCbiDto<ClassLocationStaff | null>(data, "generated.dto.ClassLocationStaff");
  }

  async getApClassStaffInstructorId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclass/staff/${id}/instructorid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getApClassStaffSessions(id: number, options?: ApClassSessionFetchOptions): Promise<Array<ApClassSessionStaff>> {
    const { data } = await this.client.get<unknown>(`/apclass/staff/${id}/sessions`, { params: serializeCbiDto(options, "generated.dto.ApClassSessionFetchOptions") });
    return deserializeCbiDto<Array<ApClassSessionStaff>>(data, "generated.dto.ApClassSessionStaff");
  }

  async getApClassStaffSignups(id: number, options?: ApClassSignupStaffFetchOptions): Promise<Array<ApClassSignupStaff>> {
    const { data } = await this.client.get<unknown>(`/apclass/staff/${id}/signups`, { params: serializeCbiDto(options, "generated.dto.ApClassSignupStaffFetchOptions") });
    return deserializeCbiDto<Array<ApClassSignupStaff>>(data, "generated.dto.ApClassSignupStaff");
  }

  async getApClassStaffTypeId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff> {
    const { data } = await this.client.get<unknown>(`/apclass/staff/${id}/typeid`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<ApClassTypeStaff>(data, "generated.dto.ApClassTypeStaff");
  }

  async getApClassSessionStaffInstanceId(id: number, options?: ApClassFetchOptions): Promise<ApClassStaff> {
    const { data } = await this.client.get<unknown>(`/apclasssession/staff/${id}/instanceid`, { params: serializeCbiDto(options, "generated.dto.ApClassFetchOptions") });
    return deserializeCbiDto<ApClassStaff>(data, "generated.dto.ApClassStaff");
  }

  async getApClassSessionStaffAttendance(id: number, options?: ApClassAttendanceFetchOptions): Promise<Array<ApClassAttendanceStaff>> {
    const { data } = await this.client.get<unknown>(`/apclasssession/staff/${id}/attendance`, { params: serializeCbiDto(options, "generated.dto.ApClassAttendanceFetchOptions") });
    return deserializeCbiDto<Array<ApClassAttendanceStaff>>(data, "generated.dto.ApClassAttendanceStaff");
  }

  async getApClassSignupStaffWlResult(id: number, options?: ApClassWlResultFetchOptions): Promise<ApClassWlResultStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclasssignup/staff/${id}/wlresult`, { params: serializeCbiDto(options, "generated.dto.ApClassWlResultFetchOptions") });
    return deserializeCbiDto<ApClassWlResultStaff | null>(data, "generated.dto.ApClassWlResultStaff");
  }

  async getApClassSignupStaffInstanceId(id: number, options?: ApClassFetchOptions): Promise<ApClassStaff> {
    const { data } = await this.client.get<unknown>(`/apclasssignup/staff/${id}/instanceid`, { params: serializeCbiDto(options, "generated.dto.ApClassFetchOptions") });
    return deserializeCbiDto<ApClassStaff>(data, "generated.dto.ApClassStaff");
  }

  async getApClassSignupStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclasssignup/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getApClassSignupStaffAttendance(id: number, options?: ApClassAttendanceFetchOptions): Promise<Array<ApClassAttendanceStaff>> {
    const { data } = await this.client.get<unknown>(`/apclasssignup/staff/${id}/attendance`, { params: serializeCbiDto(options, "generated.dto.ApClassAttendanceFetchOptions") });
    return deserializeCbiDto<Array<ApClassAttendanceStaff>>(data, "generated.dto.ApClassAttendanceStaff");
  }

  async getApClassAttendanceStaffSessionId(id: number, options?: ApClassSessionFetchOptions): Promise<ApClassSessionStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclassattendance/staff/${id}/sessionid`, { params: serializeCbiDto(options, "generated.dto.ApClassSessionFetchOptions") });
    return deserializeCbiDto<ApClassSessionStaff | null>(data, "generated.dto.ApClassSessionStaff");
  }

  async getApClassAttendanceStaffSignupId(id: number, options?: ApClassSignupStaffFetchOptions): Promise<ApClassSignupStaff | null> {
    const { data } = await this.client.get<unknown>(`/apclassattendance/staff/${id}/signupid`, { params: serializeCbiDto(options, "generated.dto.ApClassSignupStaffFetchOptions") });
    return deserializeCbiDto<ApClassSignupStaff | null>(data, "generated.dto.ApClassSignupStaff");
  }

  async getApClassWlResultStaffSignupId(id: number, options?: ApClassSignupStaffFetchOptions): Promise<ApClassSignupStaff> {
    const { data } = await this.client.get<unknown>(`/apclasswlresult/staff/${id}/signupid`, { params: serializeCbiDto(options, "generated.dto.ApClassSignupStaffFetchOptions") });
    return deserializeCbiDto<ApClassSignupStaff>(data, "generated.dto.ApClassSignupStaff");
  }

  async getClassInstanceStaff(ref: ApClassRef): Promise<ApClassStaff> {
    const { data } = await this.client.get<unknown>(`/apclass/staff/getclassinstance`, { params: { ref: encodeRequestParam(ref, "generated.dto.ApClassRef") } });
    let result = data as ApClassStaff;
    result = deserializeCbiDto<ApClassStaff>(result, "generated.dto.ApClassStaff");
    return result;
  }

  async getClassInstanceMember(ref: ApClassRef): Promise<ApClassMember> {
    const { data } = await this.client.get<unknown>(`/apclass/member/getclassinstance`, { params: { ref: encodeRequestParam(ref, "generated.dto.ApClassRef") } });
    let result = data as ApClassMember;
    result = deserializeCbiDto<ApClassMember>(result, "generated.dto.ApClassMember");
    return result;
  }

  async summaryLabelStaff(target: ApClassRef): Promise<string> {
    const { data } = await this.client.post<unknown>(`/apclass/staff/summarylabel`, serializeCbiDto(target, "generated.dto.ApClassRef"));
    return data as string;
  }

  async summaryLabelMember(target: ApClassRef): Promise<string> {
    const { data } = await this.client.post<unknown>(`/apclass/member/summarylabel`, serializeCbiDto(target, "generated.dto.ApClassRef"));
    return data as string;
  }
}

export const useApClassApi = (): ApClassApi => new ApClassApiClient();
